/**
 * The week / day grid.
 *
 * Three things about it are Persian-specific and all three live in
 * `lib/format.js` rather than here: the week starts on SATURDAY, the headers
 * are Jalali, and the digits are Persian. Every date library in the ecosystem
 * defaults to a Sunday or Monday week, so getting this wrong is the default
 * outcome — which is why the component asks for `persianWeekDays()` instead of
 * doing arithmetic of its own.
 *
 * RTL falls out of the document direction: the day columns are flex children in
 * calendar order, and an RTL container lays the first one out on the right,
 * where شنبه belongs. There is no `flex-row-reverse` anywhere.
 *
 * POSITIONING uses `calc(... * var(--mh-density-hour))` rather than pixel
 * arithmetic. One hour is the unit, and it is a design token: changing the
 * grid's density is a token edit, not a hunt through this file. (It is also
 * what keeps `npm run lint` — which fails on any raw px in app code — happy,
 * and that rule is why the constraint is worth having.)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CALENDAR_SNAP_MINUTES,
  DAY_END_HOUR,
  DAY_START_HOUR,
  DEFAULT_ACTIVITY_MINUTES,
} from '../../constants/density.js';
import {
  WEEKDAYS_FA,
  WEEKEND_INDEX,
  formatDayHeader,
  formatGregorian,
  isSameDay,
  persianWeekday,
  toPersianDigits,
} from '../../lib/format.js';
import { S } from '../../lib/strings.js';
import { ActivityChip } from './ActivityChip.web.jsx';

const HOURS = Array.from(
  { length: DAY_END_HOUR - DAY_START_HOUR },
  (_, index) => DAY_START_HOUR + index,
);

/** Minutes since the top of the grid, snapped. */
function snap(minutes) {
  return Math.round(minutes / CALENDAR_SNAP_MINUTES) * CALENDAR_SNAP_MINUTES;
}

/** A y offset inside a day column -> minutes past DAY_START_HOUR. */
function minutesFromOffset(offsetY, columnHeight) {
  const totalMinutes = (DAY_END_HOUR - DAY_START_HOUR) * 60;
  const ratio = Math.min(Math.max(offsetY / columnHeight, 0), 1);
  return snap(ratio * totalMinutes);
}

function atMinutes(day, minutes) {
  const out = new Date(day);
  out.setHours(DAY_START_HOUR, 0, 0, 0);
  out.setMinutes(out.getMinutes() + minutes);
  return out;
}

/** Where an activity sits in its column, as fractions of an hour. */
function placement(activity) {
  const start = new Date(activity.startsAt);
  const end = activity.endsAt
    ? new Date(activity.endsAt)
    : new Date(start.getTime() + DEFAULT_ACTIVITY_MINUTES * 60_000);
  const startHours = start.getHours() + start.getMinutes() / 60 - DAY_START_HOUR;
  const endHours = end.getHours() + end.getMinutes() / 60 - DAY_START_HOUR;
  return {
    top: Math.max(startHours, 0),
    // A floor so a fifteen-minute call is still readable rather than a hairline.
    height: Math.max(endHours - Math.max(startHours, 0), 0.5),
  };
}

/**
 * Lay overlapping activities side by side.
 *
 * Two meetings at ten o'clock must both be visible; stacking them would hide
 * one entirely, and a calendar that hides a meeting is worse than no calendar.
 * A simple sweep is enough at the density a person actually books.
 */
function withColumns(activities) {
  const sorted = [...activities].sort(
    (a, b) => new Date(a.startsAt) - new Date(b.startsAt),
  );
  const lanes = [];
  return sorted.map((activity) => {
    const place = placement(activity);
    const end = place.top + place.height;
    let lane = lanes.findIndex((laneEnd) => laneEnd <= place.top + 0.01);
    if (lane === -1) {
      lane = lanes.length;
      lanes.push(end);
    } else {
      lanes[lane] = end;
    }
    return { activity, place, lane };
  });
}

function laneStyle(lane, laneCount) {
  const width = 100 / laneCount;
  // `insetInlineStart`, not `left`: in an RTL column the lanes must run from
  // the right edge, and a physical property would put them on the wrong side.
  return {
    insetInlineStart: `${lane * width}%`,
    width: `${width}%`,
  };
}

export function WeekGrid({
  days,
  activities,
  colorForType,
  onCreate,
  onOpen,
  onMove,
  readOnly = false,
}) {
  const [drag, setDrag] = useState(null);
  const columnRefs = useRef({});
  /**
   * The live drag, for the HANDLERS to read.
   *
   * `drag` state drives the preview; this ref is what `mouseup` consults. They
   * are not redundant: a state update from `mousemove` is scheduled, not
   * applied, so a `mouseup` arriving in the same React batch would read the
   * position from before the last movement and drop it. The browser normally
   * delivers the two as separate tasks so the state would flush in between —
   * but "normally" is doing a lot of work in that sentence, and a ref costs
   * nothing.
   */
  const dragRef = useRef(null);

  const updateDrag = useCallback((next) => {
    dragRef.current = typeof next === 'function' ? next(dragRef.current) : next;
    setDrag(dragRef.current);
  }, []);

  const byDay = useMemo(() => {
    const map = new Map(days.map((day) => [day.toDateString(), []]));
    for (const activity of activities) {
      if (activity.allDay) continue;
      const key = new Date(activity.startsAt).toDateString();
      if (map.has(key)) map.get(key).push(activity);
    }
    return map;
  }, [days, activities]);

  const allDay = useMemo(() => {
    const map = new Map(days.map((day) => [day.toDateString(), []]));
    for (const activity of activities) {
      if (!activity.allDay) continue;
      const key = new Date(activity.startsAt).toDateString();
      if (map.has(key)) map.get(key).push(activity);
    }
    return map;
  }, [days, activities]);

  /**
   * Drag lives on `window`, not on the column.
   *
   * A pointer that leaves the column mid-drag — which it does constantly near
   * an edge — would otherwise strand the gesture with no mouseup, leaving a
   * ghost selection the user cannot clear.
   */
  useEffect(() => {
    if (!drag) return undefined;

    function onMove_(event) {
      const current = dragRef.current;
      if (!current) return;
      const column = columnRefs.current[current.dayKey];
      if (!column) return;
      const rect = column.getBoundingClientRect();
      const minutes = minutesFromOffset(event.clientY - rect.top, rect.height);
      updateDrag((previous) => (previous ? { ...previous, currentMinutes: minutes } : previous));
    }

    function onUp() {
      // Read from the REF, not the closure: this handler was installed when the
      // drag began, and the closed-over `drag` is that first frame's value.
      const finished = dragRef.current;
      updateDrag(null);
      if (!finished) return;

      const day = days.find((d) => d.toDateString() === finished.dayKey);
      if (!day) return;

      if (finished.kind === 'create') {
        const from = Math.min(finished.startMinutes, finished.currentMinutes);
        const to = Math.max(finished.startMinutes, finished.currentMinutes);
        onCreate?.({
          startsAt: atMinutes(day, from),
          // A click (no movement) means "an hour here", which is what someone
          // clicking an empty slot almost always wants.
          endsAt: atMinutes(day, to > from ? to : from + DEFAULT_ACTIVITY_MINUTES),
        });
      } else if (
        finished.kind === 'move' &&
        finished.currentMinutes !== finished.startMinutes
      ) {
        onMove?.(finished.activity, finished.currentMinutes - finished.startMinutes, day);
      }
    }

    window.addEventListener('mousemove', onMove_);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove_);
      window.removeEventListener('mouseup', onUp);
    };
    // Depends on whether a drag is IN PROGRESS, not on its position: the
    // handlers read the position from the ref, so re-subscribing on every
    // mousemove would be pure churn.
  }, [drag !== null, days, onCreate, onMove, updateDrag]);

  const startCreate = useCallback(
    (event, day) => {
      if (readOnly || event.button !== 0) return;
      const rect = event.currentTarget.getBoundingClientRect();
      const minutes = minutesFromOffset(event.clientY - rect.top, rect.height);
      updateDrag({
        kind: 'create',
        dayKey: day.toDateString(),
        startMinutes: minutes,
        currentMinutes: minutes,
      });
    },
    [readOnly, updateDrag],
  );

  const startMove = useCallback(
    (event, activity, day) => {
      if (readOnly || event.button !== 0) return;
      event.stopPropagation();
      const column = columnRefs.current[day.toDateString()];
      if (!column) return;
      const rect = column.getBoundingClientRect();
      const minutes = minutesFromOffset(event.clientY - rect.top, rect.height);
      updateDrag({
        kind: 'move',
        dayKey: day.toDateString(),
        activity,
        startMinutes: minutes,
        currentMinutes: minutes,
      });
    },
    [readOnly, updateDrag],
  );

  const today = new Date();

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Day headers. First child = rightmost in RTL, which is شنبه. */}
      <div className="flex border-b border-border bg-surface">
        <div className="w-gutter shrink-0" />
        {days.map((day, index) => {
          const isToday = isSameDay(day, today);
          return (
            <div
              key={day.toISOString()}
              className={[
                'flex flex-1 flex-col items-center border-s border-border py-2',
                index === WEEKEND_INDEX ? 'bg-surface-hover' : '',
                isToday ? 'text-primary' : '',
              ].join(' ')}
            >
              <span className="text-xs text-text-muted">{WEEKDAYS_FA[index]}</span>
              <span className={`text-sm ${isToday ? 'font-bold' : 'font-medium'}`}>
                {formatDayHeader(day)}
              </span>
              {/* The Gregorian day BENEATH the Jalali one, smaller and muted:
                  a Persian user books in Shamsi, and a congress invitation, a
                  Google Calendar entry or a foreign supplier's email arrives in
                  Gregorian. The pairing removes the translation people were
                  doing in their heads; the size ordering says which calendar
                  the app is in. No year — it is a column header. */}
              <span dir="ltr" className="text-xs text-text-muted">
                {formatGregorian(day, { withYear: false })}
              </span>
            </div>
          );
        })}
      </div>

      {/* All-day band. Present even when empty so the grid does not jump when
          somebody adds one. */}
      <div className="flex border-b border-border bg-surface">
        <div className="flex w-gutter shrink-0 items-center justify-center py-1 text-xs text-text-muted">
          {S.calendar.allDay}
        </div>
        {days.map((day) => (
          <div
            key={day.toISOString()}
            className="flex min-h-row flex-1 flex-col gap-1 border-s border-border p-1"
          >
            {(allDay.get(day.toDateString()) ?? []).map((activity) => (
              <ActivityChip
                key={activity.id}
                activity={activity}
                color={colorForType(activity.type?.id)}
                compact
                onClick={() => onOpen?.(activity)}
              />
            ))}
          </div>
        ))}
      </div>

      {/* The hour grid. The only scroll region on the screen. */}
      <div className="flex flex-1 overflow-y-auto">
        <div className="w-gutter shrink-0">
          {HOURS.map((hour) => (
            <div
              key={hour}
              className="h-hour border-b border-s border-border pe-2 text-end text-xs
                         leading-none text-text-muted"
            >
              <span className="relative -top-px" dir="ltr">
                {toPersianDigits(String(hour).padStart(2, '0'))}
              </span>
            </div>
          ))}
        </div>

        {days.map((day, index) => {
          const key = day.toDateString();
          const laid = withColumns(byDay.get(key) ?? []);
          const laneCount = Math.max(1, ...laid.map((entry) => entry.lane + 1));
          const dragging = drag?.dayKey === key;

          return (
            <div
              key={day.toISOString()}
              ref={(node) => {
                columnRefs.current[key] = node;
              }}
              onMouseDown={(event) => startCreate(event, day)}
              className={[
                'relative flex-1 border-s border-border',
                index === WEEKEND_INDEX ? 'bg-surface-hover' : 'bg-surface',
                readOnly ? '' : 'cursor-crosshair',
              ].join(' ')}
            >
              {HOURS.map((hour) => (
                <div key={hour} className="h-hour border-b border-border" />
              ))}

              {/* The drag preview, so a gesture shows what it will produce. */}
              {dragging && drag.kind === 'create' && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 rounded border
                             border-primary bg-primary/20"
                  style={{
                    top: `calc(${Math.min(drag.startMinutes, drag.currentMinutes) / 60} * var(--mh-density-hour))`,
                    height: `calc(${Math.max(Math.abs(drag.currentMinutes - drag.startMinutes) / 60, 0.25)} * var(--mh-density-hour))`,
                  }}
                />
              )}

              {laid.map(({ activity, place, lane }) => {
                const shift =
                  dragging && drag.kind === 'move' && drag.activity?.id === activity.id
                    ? (drag.currentMinutes - drag.startMinutes) / 60
                    : 0;
                return (
                  <div
                    key={activity.id}
                    className="absolute px-px"
                    style={{
                      top: `calc(${place.top + shift} * var(--mh-density-hour))`,
                      height: `calc(${place.height} * var(--mh-density-hour))`,
                      ...laneStyle(lane, laneCount),
                    }}
                  >
                    <ActivityChip
                      activity={activity}
                      color={colorForType(activity.type?.id)}
                      onMouseDown={(event) => startMove(event, activity, day)}
                      onClick={() => onOpen?.(activity)}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
